import { useState, useEffect, useCallback, useRef } from 'react';
import { X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function SocialProofToast() {
  const [notifications, setNotifications] = useState([]);
  const [current, setCurrent] = useState(null);
  const [visible, setVisible] = useState(false);
  const [index, setIndex] = useState(0);
  const [sessionCount, setSessionCount] = useState(0);
  const dismissedRef = useRef(false);
  const [dismissed, setDismissed] = useState(false);

  const MAX_PER_SESSION = 5;

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await fetch(`${API}/social-proof`);
        const data = await res.json();
        setNotifications(data.notifications || []);
      } catch {}
    };
    fetchNotifications();
  }, []);

  const showNext = useCallback(() => {
    if (dismissedRef.current || sessionCount >= MAX_PER_SESSION || notifications.length === 0) return;
    const notif = notifications[index % notifications.length];
    setCurrent(notif);
    setVisible(true);
    setSessionCount(c => c + 1);

    // Hide after 5 seconds
    setTimeout(() => setVisible(false), 5000);
    setIndex(i => i + 1);
  }, [dismissed, sessionCount, notifications, index]);

  useEffect(() => {
    if (notifications.length === 0 || dismissedRef.current) return;

    // First notification after 8 seconds
    const initialDelay = setTimeout(() => {
      showNext();
    }, 8000);

    return () => clearTimeout(initialDelay);
  }, [notifications, dismissed]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (notifications.length === 0 || dismissedRef.current || sessionCount === 0) return;
    if (sessionCount >= MAX_PER_SESSION) return;

    // Subsequent notifications every 15-20 seconds
    const interval = setTimeout(() => {
      showNext();
    }, 15000 + Math.random() * 5000);

    return () => clearTimeout(interval);
  }, [sessionCount, dismissed, notifications, showNext]);

  if (!current || dismissed) return null;

  return (
    <div
      className={`fixed bottom-6 left-6 z-50 transition-all duration-500 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}
      data-testid="social-proof-toast"
    >
      <div className="bg-[#111827]/95 backdrop-blur-xl border border-white/10 rounded-2xl px-5 py-4 shadow-2xl shadow-black/40 max-w-xs">
        <button
          onClick={() => { dismissedRef.current = true; setDismissed(true); setVisible(false); }}
          className="absolute -top-2 -right-2 w-6 h-6 bg-slate-800 border border-white/10 rounded-full flex items-center justify-center text-slate-500 hover:text-white transition-colors"
          data-testid="dismiss-social-proof"
        >
          <X className="w-3 h-3" />
        </button>

        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 border border-white/10">
            <img
              src={`https://flagcdn.com/w80/${current.country_code}.png`}
              alt={current.country_code}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-semibold leading-tight">
              <span className="text-teal-400">{current.name}</span>{' '}
              <span className="text-slate-300 font-normal">ha attivato una eSIM per</span>
            </p>
            <p className="text-white text-sm font-bold">{current.destination}</p>
            <p className="text-slate-500 text-[11px] mt-0.5">{current.minutes_ago} min fa</p>
          </div>
        </div>
      </div>
    </div>
  );
}
